/**
 * ClinicianAvatar — square (or round) photo for a clinician. Matches by
 * name to known clinicians and falls back to initials on a sunken tile.
 * The backend doesn't expose avatar_url today (see `ThreadParticipant` in
 * `@/types/api`) so the mapping is local; once it does, prefer the
 * `src` prop and drop the name match.
 */

import { cn } from '@/lib/cn';

const KNOWN: { match: RegExp; src: string }[] = [
  { match: /hinds/i, src: '/brand/hinds.webp' },
];

function resolveSrc(name: string | null | undefined, src?: string): string | null {
  if (src) return src;
  if (!name) return null;
  const hit = KNOWN.find((k) => k.match.test(name));
  return hit?.src ?? null;
}

function initials(name: string | null | undefined): string {
  if (!name) return '';
  return name
    .replace(/^(Dr\.?|Mr\.?|Ms\.?|Mrs\.?)\s+/i, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

export function ClinicianAvatar({
  name,
  src,
  size = 40,
  shape = 'round',
  className,
}: {
  name?: string | null;
  src?: string;
  size?: number;
  shape?: 'square' | 'round';
  className?: string;
}) {
  const resolved = resolveSrc(name, src);
  const sharedClass = cn(
    'shrink-0 overflow-hidden bg-[var(--surface-sunken)] flex items-center justify-center',
    shape === 'round' ? 'rounded-full ring-1 ring-line-subtle' : '',
    className,
  );

  if (resolved) {
    return (
      <img
        src={resolved}
        alt={name ?? 'Clinician'}
        width={size}
        height={size}
        className={cn(sharedClass, 'object-cover object-top')}
        style={{ width: size, height: size }}
        draggable={false}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(sharedClass, 'type-label-xs text-ink-muted')}
      style={{ width: size, height: size }}
    >
      {initials(name)}
    </span>
  );
}
